import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaTimes, FaCrown, FaMedal, FaGem, FaStar } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

function Plans() {
  const [plansData, setPlansData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const navigate = useNavigate();

  // All possible features to compare against
 const allFeatures = [
  "Home", "Student Management", "Attendance", 
  "Inventory", "Time Table", "MDM", "Result",
  "Fee Management", "Account", "Payroll",
  "Student App", "Teacher App", "WhatsApp API Integration", "Unlimited Access to all features"
];


  // Default plan features configuration for our specific plans
const defaultPlanFeatures = {
  "Silver": {
    studentLimit: "Up to 100 students",
    icon: <FaMedal className="text-secondary" />,
    features: [
      "Home", "Student Management", "Attendance", 
      "Inventory", "Time Table"
    ]
  },
  "Gold": {
    studentLimit: "Up to 500 students",
    icon: <FaGem className="text-warning" />,
    features: [
      "Home", "Student Management", "Attendance", 
      "Inventory", "Time Table", "MDM", "Result"
    ]
  },
  "Platinum": {
    studentLimit: "Up to 1000 students",
    icon: <FaStar className="text-info" />,
    features: [
      "Home", "Student Management", "Attendance", 
      "Inventory", "Time Table", "MDM", "Result",
      "Fee Management", "Account", "Payroll",
      "Student App", "Teacher App"
    ]
  },
  "Platinum Plus": {
    studentLimit: "1000+ students (Unlimited)",
    icon: <FaCrown className="text-black" />,
    features: [
      "Home", "Student Management", "Attendance", 
      "Inventory", "Time Table", "MDM", "Result",
      "Fee Management", "Account", "Payroll",
      "Student App", "Teacher App", "WhatsApp API Integration","Unlimited Access to all features",
    ]
  } 
};


  // The specific plans we want to show
  const plansToShow = ["Silver", "Gold", "Platinum", "Platinum Plus"];

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const udiseNumber = localStorage.getItem('udiseNumber');
        let fetchedPlans = null;

        // First try to fetch from user's UDISE number
        if (udiseNumber) {
          const userResponse = await fetch(
            `https://digischoolweb-default-rtdb.firebaseio.com/Admin/${udiseNumber}/purchesPlan.json`
          );
          const userData = await userResponse.json();
          
          if (userData) {
            // Filter to only include our specific plans
            const filteredPlans = {};
            plansToShow.forEach(plan => {
              if (userData[plan]) {
                filteredPlans[plan] = userData[plan];
              }
            });
            
            if (Object.keys(filteredPlans).length > 0) {
              setPlansData(filteredPlans);
              setLoading(false);
              return;
            }
          }
        }

        // If no user-specific plans, fetch from general Admin/purchesPlan
        const adminResponse = await fetch(
          'https://digischoolweb-default-rtdb.firebaseio.com/Admin/purchesPlan.json'
        );
        const adminData = await adminResponse.json();
        
        // Filter to only include our specific plans
        const filteredPlans = {};
        if (adminData) {
          plansToShow.forEach(plan => {
            if (adminData[plan]) {
              filteredPlans[plan] = adminData[plan];
            }
          });
        }

        // If we got any plans from the database, use them
        if (Object.keys(filteredPlans).length > 0) {
          setPlansData(filteredPlans);
        } else {
          // Otherwise use our default data for these specific plans
          setPlansData({
            "Silver": { mrp: "3500", fixRate: "1000", earlyRenewal: "1000" },
            "Gold": { mrp: "15000", fixRate: "2000", earlyRenewal: "2000" },
            "Platinum": { mrp: "40000", fixRate: "3000", earlyRenewal: "3000" },
            "Platinum Plus": { mrp: "80000", fixRate: "2500", earlyRenewal: "2500" }
          });
        }
        
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            duration: 1.5,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            border: '5px solid #f3f3f3',
            borderTop: '5px solid #B22234',
            borderRight: '5px solid #B22234'
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="alert alert-danger mx-3 my-5"
      >
        Error loading plans: {error}
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="main-content-of-page" 
      style={{ 
        padding: '20px', 
        maxWidth: '1700px', 
        margin: '0 auto',
        minHeight: '100vh'
      }}
    >
      {/* Header and back button */}
      <motion.div 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', marginTop: '50px' }}
      >
        <h2 style={{ 
          fontSize: '2rem',
          fontWeight: '700',
          color: '#143625',
          textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
        }}>
          Subscription Plans
        </h2>
        <motion.button 
          whileHover={{ scale: 1.05, boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
          whileTap={{ scale: 0.95 }}
          className="btn btn-secondary" 
          onClick={() => navigate(-1)}
          style={{
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '600'
          }}
        >
          Back
        </motion.button>
      </motion.div>

      {/* Plan cards */}
      <div className="row" style={{ gap: '20px 0' }}>
        <AnimatePresence>
          {plansToShow.map((plan, index) => {
            // Skip if this plan doesn't exist in our data
            if (!plansData || !plansData[plan]) return null;
            
            return (
              <motion.div 
                key={plan}
                className="col-md-6 col-lg-3 mb-4"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -5 }}
                onMouseEnter={() => setHoveredPlan(plan)}
                onMouseLeave={() => setHoveredPlan(null)}
              >
                <div className="card h-100" style={{ 
                  border: 'none', 
                  borderRadius: '12px', 
                  boxShadow: hoveredPlan === plan ? 
                    `0 15px 30px -5px ${getPlanColor(plan, true)}40` : 
                    '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  transform: hoveredPlan === plan ? 'scale(1.02)' : 'scale(1)',
                  overflow: 'hidden'
                }}>
                  {/* Card Header */}
                  <motion.div 
                    className="card-header" 
                    style={{ 
                      background: `linear-gradient(135deg, ${getPlanColor(plan)} 0%, ${getPlanColor(plan, true)} 100%)`,
                      color: 'white',
                      borderTopLeftRadius: '12px',
                      borderTopRightRadius: '12px',
                      padding: '20px',
                      textAlign: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <motion.div 
                      animate={hoveredPlan === plan ? { scale: 1.1 } : { scale: 1 }}
                      transition={{ duration: 0.3 }}
                      style={{ 
                        position: 'absolute',
                        top: '-50px',
                        right: '-50px',
                        width: '100px',
                        height: '100px',
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.1)'
                      }}
                    />
                    <motion.div 
                      animate={hoveredPlan === plan ? { scale: 1.1 } : { scale: 1 }}
                      transition={{ duration: 0.3 }}
                      style={{ 
                        position: 'absolute',
                        bottom: '-30px',
                        left: '-30px',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.1)'
                      }}
                    />
                    <div style={{ 
                      fontSize: '2rem', 
                      marginBottom: '10px',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      {defaultPlanFeatures[plan]?.icon || <FaStar />}
                    </div>
                    <h4 style={{ 
                      margin: 0,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
                    }}>
                      {plan} Plan
                    </h4>
                  </motion.div>

                  {/* Card Body */}
                  <div className="card-body" style={{ padding: '25px' }}>
                    {/* Pricing */}
                    <motion.div 
                      className="text-center mb-4"
                      whileHover={{ scale: 1.05 }}
                    >
                      <h3 style={{ 
                        color: getPlanColor(plan),
                        fontWeight: '700',
                        marginBottom: '5px',
                        fontSize: '2rem'
                      }}>
                        ₹{plansData[plan]?.mrp || 'N/A'}
                      </h3>
                      <small style={{ 
                        color: '#7f8c8d',
                        fontSize: '0.85rem'
                      }}>
                        Early Renewal: ₹{plansData[plan]?.fixRate || 'N/A'}
                      </small>
                    </motion.div>
                    
                    {/* Student Limit */}
                    <motion.div 
                      className="mb-4 text-center"
                      whileHover={{ scale: 1.02 }}
                    >
                      <h5 style={{ 
                        color: '#143625',
                        fontWeight: '600',
                        marginBottom: '10px'
                      }}>
                        Student Limit
                      </h5>
                      <p style={{ 
                        color: '#7f8c8d',
                        marginBottom: 0,
                        fontSize: '0.95rem'
                      }}>
                        {defaultPlanFeatures[plan]?.studentLimit || 'N/A'}
                      </p>
                    </motion.div>

                    {/* Features List */}
                    <div>
                      <h5 className="text-center" style={{ 
                        color: '#143625',
                        fontWeight: '600',
                        marginBottom: '15px'
                      }}>
                        Features
                      </h5>
                      <ul style={{ 
                        listStyle: 'none',
                        padding: 0,
                        margin: 0
                      }}>
                        {allFeatures.map((feature, index) => {
                          const isIncluded = defaultPlanFeatures[plan]?.features?.includes(feature) || false;
                          return (
                            <motion.li 
                              key={index} 
                              style={{ 
                                padding: '8px 0',
                                display: 'flex',
                                alignItems: 'center',
                                borderBottom: '1px solid #f1f1f1'
                              }}
                              whileHover={{ 
                                backgroundColor: 'rgba(0,0,0,0.02)',
                                paddingLeft: '5px'
                              }}
                            >
                              <motion.span 
                                style={{ 
                                  marginRight: '10px',
                                  color: isIncluded ? '#2ecc71' : '#e74c3c',
                                  fontSize: '1.1rem'
                                }}
                                animate={isIncluded ? { 
                                  scale: [1, 1.2, 1],
                                  rotate: [0, 10, -10, 0]
                                } : {}}
                                transition={{ duration: 0.5 }}
                              >
                                {isIncluded ? <FaCheck /> : <FaTimes />}
                              </motion.span>
                              <span style={{
                                color: isIncluded ? '#143625' : '#95a5a6',
                                fontWeight: isIncluded ? '500' : '400',
                                textDecoration: isIncluded ? 'none' : 'line-through',
                                fontSize: '0.9rem'
                              }}>
                                {feature}
                              </span>
                            </motion.li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// Helper function to get plan colors
function getPlanColor(plan, lighten = false) {
  const colors = {
    "Silver": lighten ? '#bdc3c7' : '#95a5a6',
    "Gold": lighten ? '#f1c40f' : '#f39c12',
    "Platinum": lighten ? '#B22234' : '#2980b9',
    "Platinum Plus": lighten ? '#e74c3c' : '#c0392b'
  };
  return colors[plan] || '#B22234';
}

export default Plans;